// Ch78 · WHERE THEY COME FROM · WHEN THEY NEED TO MOVE · WHAT THEY WANT.
//
// Three distributions over the Leads page's population, pure and fixture-tested.
//
// Every figure here names its own denominator, because the denominator is where
// this kind of card lies. "81% real" over five leads of which four were never
// scored is not a quality signal, it is a scoring-coverage signal wearing one —
// so `realPct` is measured over the SCORED rows only and the unscored count
// rides beside it. A source with nothing scored reports null, and the card
// draws "—".
//
// Nothing is inferred from free text beyond what the field can carry: pets are
// yes / no / not stated against an explicit negation list, never guessed from
// prose; a bedroom count is the renter's own words, tidied.

import type { BoardPerson } from "./moveInBoard";
import type { WorkRow } from "./leadsWork";
import { formatLeadApartmentSize, normalizeLeadNeighborhoods } from "./leadPreferences";

const DAY_MS = 86_400_000;

export interface Bucket {
  key: string;
  label: string;
  count: number;
}

// ------------------------------------------------------------- where from

export interface SourceRow {
  key: string;
  label: string;
  /** Everyone attributed to this source. */
  leads: number;
  /** How many of those the scorer has actually looked at. */
  scored: number;
  /** Of the SCORED ones, how many read real. */
  real: number;
  /** real / scored, or null when nothing here has been scored. Never real/leads. */
  realPct: number | null;
  /** How many became clients. */
  clients: number;
}

export interface CampaignRow {
  label: string;
  leads: number;
  real: number;
  clients: number;
}

export interface SourcesModel {
  rows: SourceRow[];
  /** The single named campaign doing best, or null when none is named. */
  topCampaign: CampaignRow | null;
  /** People whose source nothing records. Counted, never dropped. */
  unattributed: number;
}

function campaignOf(person: BoardPerson): string | null {
  const lead = person.lead;
  if (!lead) return null;
  const named = (lead.source_utm_campaign || "").trim() || (lead.source_utm_content || "").trim();
  return named || null;
}

export function buildSources(rows: WorkRow[]): SourcesModel {
  const byKey = new Map<string, SourceRow>();
  const byCampaign = new Map<string, CampaignRow>();
  let unattributed = 0;

  for (const row of rows) {
    const label = row.sourceLabel;
    if (!label) {
      unattributed += 1;
      continue;
    }
    const key = label.toLowerCase();
    let entry = byKey.get(key);
    if (!entry) {
      entry = { key, label, leads: 0, scored: 0, real: 0, realPct: null, clients: 0 };
      byKey.set(key, entry);
    }
    const isClient = row.person.kind === "client";
    const scored = row.person.tier !== null;
    entry.leads += 1;
    if (scored) entry.scored += 1;
    if (row.person.tone === "real") entry.real += 1;
    if (isClient) entry.clients += 1;

    const campaign = campaignOf(row.person);
    if (campaign) {
      let c = byCampaign.get(campaign);
      if (!c) {
        c = { label: campaign, leads: 0, real: 0, clients: 0 };
        byCampaign.set(campaign, c);
      }
      c.leads += 1;
      if (row.person.tone === "real") c.real += 1;
      if (isClient) c.clients += 1;
    }
  }

  const out = [...byKey.values()];
  for (const entry of out) {
    entry.realPct = entry.scored > 0 ? Math.round((100 * entry.real) / entry.scored) : null;
  }
  out.sort((a, b) => b.clients - a.clients || b.leads - a.leads || a.label.localeCompare(b.label));

  const campaigns = [...byCampaign.values()].sort(
    (a, b) => b.clients - a.clients || b.real - a.real || b.leads - a.leads,
  );
  return { rows: out, topCampaign: campaigns[0] ?? null, unattributed };
}

// ------------------------------------------------------------- when to move

export const TIMING_BUCKETS: ReadonlyArray<{ key: string; label: string }> = [
  { key: "next30", label: "Next 30 days" },
  { key: "d31_60", label: "31–60 days" },
  { key: "d61_90", label: "61–90 days" },
  { key: "d90plus", label: "90+ days" },
  { key: "past", label: "Past due" },
  { key: "none", label: "No date" },
];

/** The calendar's people as a distribution — the same rows, counted by urgency. */
export function buildTiming(people: BoardPerson[], now: Date): Bucket[] {
  const todayMs = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const counts: Record<string, number> = { next30: 0, d31_60: 0, d61_90: 0, d90plus: 0, past: 0, none: 0 };
  for (const p of people) {
    if (p.dateMs === null) {
      counts.none += 1;
      continue;
    }
    const days = Math.round((p.dateMs - todayMs) / DAY_MS);
    if (days < 0) counts.past += 1;
    else if (days <= 30) counts.next30 += 1;
    else if (days <= 60) counts.d31_60 += 1;
    else if (days <= 90) counts.d61_90 += 1;
    else counts.d90plus += 1;
  }
  return TIMING_BUCKETS.map((b) => ({ key: b.key, label: b.label, count: counts[b.key] }));
}

// ---------------------------------------------------------------- what they want

export const BUDGET_BANDS: ReadonlyArray<{ key: string; label: string; min: number; max: number }> = [
  { key: "u1000", label: "Under $1,000", min: 0, max: 999 },
  { key: "b1000", label: "$1,000–1,499", min: 1000, max: 1499 },
  { key: "b1500", label: "$1,500–1,999", min: 1500, max: 1999 },
  { key: "b2000", label: "$2,000–2,999", min: 2000, max: 2999 },
  { key: "b3000", label: "$3,000+", min: 3000, max: Infinity },
];

/** Explicit, and short on purpose: anything not on this list is not a refusal. */
const PET_NEGATIONS = new Set(["no", "none", "no pets", "n/a", "na", "nope", "false", "0"]);

export type PetsAnswer = "yes" | "no" | "unknown";

/**
 * Did they tell us about pets? Read from `leads.pets`, which is free-ish text.
 * A value we do not recognise as a negation is a POSITIVE answer ("cat", "2
 * dogs"), an empty field is UNKNOWN, and nothing here is inferred from notes.
 */
export function leadPetsAnswer(value: unknown): PetsAnswer {
  if (value === null || value === undefined) return "unknown";
  if (typeof value === "boolean") return value ? "yes" : "no";
  const text = Array.isArray(value) ? value.join(", ") : String(value);
  const trimmed = text.trim().toLowerCase();
  if (!trimmed) return "unknown";
  return PET_NEGATIONS.has(trimmed) ? "no" : "yes";
}

export interface WantsModel {
  budgets: Bucket[];
  /** How many stated no budget at all — the band chart's missing denominator. */
  budgetNotStated: number;
  neighborhoods: Bucket[];
  /** People who named no area. */
  areaNotStated: number;
  pets: { yes: number; no: number; unknown: number };
  bedrooms: Bucket[];
  bedroomsNotStated: number;
}

export function buildWants(rows: WorkRow[], opts: { topAreas?: number } = {}): WantsModel {
  const topAreas = opts.topAreas ?? 5;
  const budgets = BUDGET_BANDS.map((b) => ({ key: b.key, label: b.label, count: 0 }));
  const areas = new Map<string, Bucket>();
  const beds = new Map<string, Bucket>();
  const pets = { yes: 0, no: 0, unknown: 0 };
  let budgetNotStated = 0;
  let areaNotStated = 0;
  let bedroomsNotStated = 0;

  for (const row of rows) {
    const p = row.person;
    if (p.budget === null) budgetNotStated += 1;
    else {
      const band = BUDGET_BANDS.findIndex((b) => p.budget! >= b.min && p.budget! <= b.max);
      if (band >= 0) budgets[band].count += 1;
    }

    const named = p.neighborhoods.length
      ? p.neighborhoods
      : p.lead
        ? normalizeLeadNeighborhoods(p.lead.neighborhoods)
        : [];
    if (!named.length) areaNotStated += 1;
    for (const area of named) {
      const key = area.toLowerCase();
      const entry = areas.get(key) ?? { key, label: area, count: 0 };
      entry.count += 1;
      areas.set(key, entry);
    }

    pets[leadPetsAnswer(p.lead?.pets)] += 1;

    const size = formatLeadApartmentSize(p.lead?.apartment_size);
    if (!size) bedroomsNotStated += 1;
    else {
      const key = size.toLowerCase();
      const entry = beds.get(key) ?? { key, label: size, count: 0 };
      entry.count += 1;
      beds.set(key, entry);
    }
  }

  return {
    budgets,
    budgetNotStated,
    neighborhoods: [...areas.values()]
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
      .slice(0, topAreas),
    areaNotStated,
    pets,
    bedrooms: [...beds.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
    bedroomsNotStated,
  };
}
